/**
 * SafeAttachment
 *
 * Drop-in replacement for Stream Chat's <Attachment /> that fixes two issues
 * with video links pasted into chat:
 *
 * 1. **Autoplay** — The YouTube IFrame API can autoplay even with `playing={false}`
 *    when a user gesture (e.g. clicking a channel from search results) is in scope.
 *    Fix: use a click-to-play poster that only embeds the IFrame after the user
 *    explicitly clicks the play button, making autoplay impossible by design.
 *
 * 2. **Unbounded sizing** — ReactPlayer is rendered with `width: 100%` /
 *    `height: 100%` which, without a constraining parent, causes the
 *    video to fill the entire screen.
 *    Fix: wrap in a max-width/max-height container.
 *
 * Passed to <Channel Attachment={SafeAttachment}> to override defaults.
 */
import { useEffect, useState } from "react";
import ReactPlayer from "react-player";
import type { ReactPlayerProps } from "react-player";
import {
  Attachment as DefaultAttachment,
  Card as DefaultCard,
} from "stream-chat-react";
import type { AttachmentProps } from "stream-chat-react";
import { AlertCircle, ExternalLink, FileText, Image, Loader2, Video } from "lucide-react";
import { getAttachmentViewingUrl } from "@/shared/services/uploadService";
import { parseAppLink } from "@/shared/utils/appLinks";

/** Extract YouTube video ID from common URL formats. */
function getYouTubeId(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.hostname === "youtu.be") return u.pathname.slice(1).split("?")[0];
    if (u.hostname.includes("youtube.com")) {
      const v = u.searchParams.get("v");
      if (v) return v;
      // Handle /embed/ID and /shorts/ID
      const m = u.pathname.match(/\/(embed|shorts)\/([^/?]+)/);
      if (m) return m[2];
    }
  } catch {}
  return null;
}

/**
 * Click-to-play wrapper for YouTube URLs.
 * Shows a thumbnail + play button until the user clicks; only then embeds
 * the actual IFrame. This prevents any form of autoplay, including the
 * browser-gesture-triggered autoplay that occurs when a channel is opened
 * from search results.
 */
const YouTubePoster = ({ url }: { url: string }) => {
  const [playing, setPlaying] = useState(false);
  const videoId = getYouTubeId(url);

  if (!videoId) {
    // Fallback for non-standard YouTube URLs
    return (
      <div style={{ maxWidth: 400, width: "100%", aspectRatio: "16/9" }}>
        <ReactPlayer
          url={url}
          playing={false}
          controls
          width="100%"
          height="100%"
          config={{ youtube: { playerVars: { autoplay: 0 } } }}
        />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 400, width: "100%", aspectRatio: "16/9", position: "relative", background: "#000", borderRadius: 4, overflow: "hidden" }}>
      {playing ? (
        <ReactPlayer
          url={url}
          playing
          controls
          width="100%"
          height="100%"
          config={{ youtube: { playerVars: { autoplay: 1 } } }}
        />
      ) : (
        <>
          <img
            src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
            alt="Video thumbnail"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
          <button
            onClick={() => setPlaying(true)}
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0,0,0,0.3)",
              border: "none",
              cursor: "pointer",
            }}
            aria-label="Play video"
          >
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
              <circle cx="32" cy="32" r="32" fill="rgba(0,0,0,0.6)" />
              <polygon points="26,20 50,32 26,44" fill="white" />
            </svg>
          </button>
        </>
      )}
    </div>
  );
};

/**
 * A ReactPlayer wrapper that prevents autoplay and constrains dimensions.
 * Overrides the `Media` sub-component used for non-YouTube media attachments.
 */
const SafeReactPlayer = (props: ReactPlayerProps) => {
  const url = typeof props.url === "string" ? props.url : "";
  if (url && getYouTubeId(url) !== null) {
    return <YouTubePoster url={url} />;
  }
  return (
    <div style={{ maxWidth: 400, width: "100%", aspectRatio: "16/9" }}>
      <ReactPlayer
        {...props}
        playing={false}
        controls
        width="100%"
        height="100%"
        config={{
          ...props.config,
          file: {
            ...props.config?.file,
            attributes: {
              ...(props.config?.file as any)?.attributes,
              autoPlay: false,
              preload: "metadata",
            },
          },
        }}
      />
    </div>
  );
};

/**
 * A custom Card component that wraps video-type card previews in a
 * constrained, non-autoplaying container. Non-video cards delegate
 * to the SDK's default Card.
 */
const SafeCard = (props: any) => {
  const { asset_url, type, title, title_link, og_scrape_url, text, author_name } = props;

  // Only intercept video-type cards that have an asset_url
  if (type === "video" && asset_url) {
    const url = title_link || og_scrape_url;
    let hostname = "";
    try { hostname = url ? new URL(url).hostname.replace(/^www\./, "") : ""; } catch {}

    return (
      <div className={`str-chat__message-attachment-card str-chat__message-attachment-card--${type}`}>
        <div className="str-chat__message-attachment-card--header str-chat__message-attachment-card-react--header">
          <YouTubePoster url={asset_url} />
        </div>
        <div className="str-chat__message-attachment-card--content">
          <div className="str-chat__message-attachment-card--flex">
            {url && (
              <div className="str-chat__message-attachment-card--source-link">
                <a
                  className="str-chat__message-attachment-card--url"
                  href={url}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {author_name || hostname}
                </a>
              </div>
            )}
            {title && <div className="str-chat__message-attachment-card--title">{title}</div>}
            {text && <div className="str-chat__message-attachment-card--text">{text}</div>}
          </div>
        </div>
      </div>
    );
  }

  // Non-video cards: use the SDK default
  return <DefaultCard {...props} />;
};

const isImageMime = (mime?: string) => (mime || "").startsWith("image/");
const isVideoMime = (mime?: string) => (mime || "").startsWith("video/");

const SharepointFileAttachment = ({ attachment }: { attachment: any }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const attachmentId = attachment.attachment_id;
  const mimeType = attachment.mime_type || attachment.file_type;
  const title = attachment.title || attachment.name || "File";
  const FileIcon = isVideoMime(mimeType) ? Video : isImageMime(mimeType) ? Image : FileText;

  useEffect(() => {
    let alive = true;
    if (!attachmentId) return;
    setUrl(null);
    setError(false);
    getAttachmentViewingUrl(attachmentId)
      .then((freshUrl) => {
        if (alive) setUrl(freshUrl);
      })
      .catch(() => {
        if (alive) setError(true);
      });
    return () => {
      alive = false;
    };
  }, [attachmentId]);

  return (
    <div className="mt-2 max-w-[420px] overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center gap-2.5 border-b border-slate-100 px-3 py-2.5">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-indigo-50 text-indigo-600">
          <FileIcon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold leading-5 text-slate-900">{title}</div>
          <div className="flex items-center gap-1.5 text-[11px] leading-4 text-slate-500">
            <span>Files</span>
            {mimeType && (
              <>
                <span className="text-slate-300">/</span>
                <span className="truncate">{mimeType}</span>
              </>
            )}
          </div>
        </div>
      </div>
      {error ? (
        <div className="flex items-center gap-2 px-3 py-3 text-sm text-red-600">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>Unable to load file</span>
        </div>
      ) : !url ? (
        <div className="flex items-center gap-2 px-3 py-3 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
          <span>Loading file...</span>
        </div>
      ) : isVideoMime(mimeType) ? (
        <video
          src={url}
          controls
          preload="metadata"
          className="block aspect-video w-full bg-black"
        />
      ) : isImageMime(mimeType) ? (
        <a href={url} target="_blank" rel="noreferrer" className="block bg-slate-50">
          <img src={url} alt={title} className="block max-h-80 w-full object-contain bg-slate-50" />
        </a>
      ) : (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="flex items-center justify-between gap-3 px-3 py-3 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-50"
        >
          <span>Open file</span>
          <ExternalLink className="h-4 w-4 shrink-0" />
        </a>
      )}
    </div>
  );
};

/**
 * Drop-in Attachment component with safe video handling.
 * Usage: <Channel Attachment={SafeAttachment}>
 */
export const SafeAttachment = (props: AttachmentProps) => {
  const sharepointAttachments = (props.attachments || []).filter(
    (att: any) => att?.type === "sharepoint_file",
  );
  // Link-preview cards for our own links only show the web app's generic page,
  // and the message text already shows the file/folder/announcement by name.
  const otherAttachments = (props.attachments || []).filter(
    (att: any) => att?.type !== "sharepoint_file" && !parseAppLink(att?.og_scrape_url),
  );

  return (
    <>
      {otherAttachments.length > 0 && (
        <DefaultAttachment
          {...props}
          attachments={otherAttachments}
          Card={SafeCard}
          Media={SafeReactPlayer}
        />
      )}
      {sharepointAttachments.map((attachment: any) => (
        <SharepointFileAttachment
          key={attachment.attachment_id || attachment.title}
          attachment={attachment}
        />
      ))}
    </>
  );
};
